"use client";

import { useEffect, useMemo } from "react";

import {
  getRequiredFieldsForDirectSalesSignUp,
} from "../validation/validateSignUpField";
import { DirectSalesFlowSlug } from "../constants";
import type { TCatFieldName } from "@./suf-store";

import { useDirectSalesFlowErrorStore } from "./useDirectSalesFlowErrorStore";
import { useDirectSalesFlowStore } from "./useDirectSalesFlowStore";

export const useDirectSalesPageStatus = (slug: DirectSalesFlowSlug) => {
  const validatedForSlug = useDirectSalesFlowErrorStore(
    (state) => state.validatedFields[slug] || [],
  );

  const validated = useMemo(() => new Set(validatedForSlug), [ validatedForSlug ]);

  const {
    activeCatId,
    cats,
    activeCatIds,
  } = useDirectSalesFlowStore((state) => ({
    activeCatId: state.activeCatId,
    cats: state.cats,
    activeCatIds: state.getActiveCatIds(),
  }));

  const requiredFields = useMemo(() => getRequiredFieldsForDirectSalesSignUp(slug), [ slug, cats ]);

  const getFieldNamesForCat = (catId: string): TCatFieldName[] => requiredFields
    .filter((field) => field.startsWith(`cat[${catId}].`))
    .map((field) => field.replace(`cat[${catId}].`, "") as TCatFieldName);

  useEffect(() => {
    const filtered = validatedForSlug.filter((field) => requiredFields.includes(field));

    if (filtered.length !== validatedForSlug.length) {
      useDirectSalesFlowErrorStore.setState((state) => ({
        validatedFields: {
          ...state.validatedFields,
          [slug]: filtered,
        },
      }));
    }
  }, [ slug, validatedForSlug, requiredFields ]);

  const isCatComplete = (catId: string): boolean => requiredFields
    .filter((key) => key.startsWith(`cat[${catId}]`))
    .every((fieldKey) => validated.has(fieldKey));

  const catCompletionMap: Record<string, boolean> = {};
  for (const catId of activeCatIds) {
    catCompletionMap[catId] = isCatComplete(catId);
  }

  const blankCatIds = activeCatIds.filter(
    (catId) => !validatedForSlug.some((field) => field.startsWith(`cat[${catId}].`)),
  );

  const isPageComplete = requiredFields.every((fieldKey) => validated.has(fieldKey));

  return {
    isPageComplete,
    isCatComplete: activeCatId ? isCatComplete(activeCatId) : false,
    catCompletionMap,
    blankCatIds,
    getFieldNamesForCat,
  };
};
